import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput
} from '@mui/material';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TabsComponent from '@components/tabs/tabs.tsx';
import { LanguageEnum } from '@models/language.model.ts';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import useSeriesStore from '@modules/entertainment/pages/series/store/series.store.ts';
import { EditDialogSeasonsFormProps } from '@modules/entertainment/models/series.model.ts';
import useLanguageStore from '@store/language-store/language.store.tsx';
import { validateNotOnlySpaces } from '@utils/trim-form-values-utils.ts';
import { seasonErrorMessages } from '@modules/entertainment/utils/series.utils.ts';

const SeasonDialogForm: React.FC<EditDialogSeasonsFormProps> = ({
  onSave,
  selectedSeason
}) => {
  const {
    register,
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({ mode: 'onTouched' });

  const getErrorMessage = useErrorMessage();

  const { t } = useTranslation();
  const { isSeasonLoading } = useSeriesStore();
  const { languageList } = useLanguageStore((state) => state);

  const defaultLang = languageList[0]?.value || LanguageEnum.English;
  const [currentTab, setCurrentTab] = React.useState<string>(defaultLang);

  const [multiLangFields, setMultiLangFields] = React.useState<{
    title: Record<string, string | undefined>;
  }>({
    title: {}
  });

  const handleChangeLanguage = (_e: React.SyntheticEvent, newLang: string) => {
    const currentTitle = watch('title');

    setMultiLangFields((prev) => ({
      title: { ...prev.title, [currentTab]: currentTitle }
    }));

    setCurrentTab(newLang);
    setValue('title', multiLangFields.title?.[newLang] || '');
  };

  const onSubmit = (data: any) => {
    const currentTitle = watch('title');

    const fullData: any = {
      title: { ...multiLangFields.title, [currentTab]: currentTitle },
      seasonNumber: data.seasonNumber
    };

    onSave(fullData);
  };

  useEffect(() => {
    if (selectedSeason && selectedSeason.title) {
      reset({
        title: selectedSeason?.title[currentTab] || '',
        seasonNumber: selectedSeason?.seasonNumber || ''
      });
    }
  }, [currentTab, selectedSeason, reset]);

  return (
    <div>
      <div className="w-full mb-4">
        <TabsComponent
          tabs={languageList}
          value={currentTab}
          onChange={handleChangeLanguage}
        />
      </div>

      <form className="flex flex-col gap-1">
        <FormControl error={!!errors.title}>
          <FormLabel className="flex">
            <span className="reuqired-symbole">*</span>
            {t('label_title')}
            <InfoTooltip text="tooltip_info_about_title" />
          </FormLabel>
          <OutlinedInput
            placeholder={t('placeholder_season_title')}
            {...register('title', {
              required: true,
              validate: {
                notOnlySpaces: validateNotOnlySpaces
              }
            })}
            error={!!errors.title}
          />
          <FormHelperText>
            {errors?.title &&
              getErrorMessage({
                errors,
                fieldName: 'title',
                errorMessages: seasonErrorMessages
              })}
          </FormHelperText>
        </FormControl>

        <FormControl error={!!errors.seasonNumber}>
          <FormLabel className="flex">
            <span className="reuqired-symbole">*</span>
            {t('label_season_number')}
            <InfoTooltip text="tooltip_info_about_season_number" />
          </FormLabel>
          <OutlinedInput
            placeholder={t('placeholder_season_number')}
            type="number"
            className="no-spinner"
            {...register('seasonNumber', {
              required: true,
              valueAsNumber: true
            })}
            error={!!errors.seasonNumber}
          />
          <FormHelperText>
            {errors?.seasonNumber &&
              getErrorMessage({
                errors,
                fieldName: 'seasonNumber',
                errorMessages: seasonErrorMessages
              })}
          </FormHelperText>
        </FormControl>

        <div className="flex justify-end items-center gap-6">
          <Button
            disabled={!isValid}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            loading={isSeasonLoading()}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SeasonDialogForm;
